import { cn } from "@/lib/cn";

/**
 * Operator toolbar shell.
 *
 * Every operator page (Inbox / Issues / Patches / Knowledge / Runs / Workspaces /
 * Projects / Follow-ups / settings tables) renders the same row of chrome:
 *
 *   [ scopes / tabs ]   [ search ]                [ saved-views ] [ columns ]
 *                                                 [ split-view ] [ export ] [ … ]
 *
 * `Toolbar` is a thin layout primitive — it doesn't own state — that
 * fixes the spacing, the wrap behaviour, and the `ml-auto` boundary that
 * separates left-aligned filtering from right-aligned utilities.
 *
 * `Toolbar.Group` standardizes the "tight cluster" pattern (h-7/h-8
 * controls glued together with gap-1 / gap-1.5).
 *
 * Pages that need a sticky toolbar should pass `stickyToolbar` to the
 * DataTable instead of wrapping this in a sticky div — the DataTable
 * already measures the toolbar height and offsets the column header.
 */
export function Toolbar({
  children,
  className,
  density = "default",
}: {
  children: React.ReactNode;
  className?: string;
  /** `compact` drops the row gap from `gap-2` to `gap-1.5` for tight pages. */
  density?: "default" | "compact";
}) {
  return (
    <div
      role="toolbar"
      className={cn(
        "flex items-center flex-wrap",
        density === "compact" ? "gap-1.5" : "gap-2",
        className,
      )}
    >
      {children}
    </div>
  );
}

/**
 * Tight cluster of controls (chips, segment buttons, icon buttons).
 * Pushes its contents to the right when `align="end"` is set so pages
 * can place the utility cluster without per-page `ml-auto` plumbing.
 */
function ToolbarGroup({
  children,
  className,
  align = "start",
}: {
  children: React.ReactNode;
  className?: string;
  align?: "start" | "end";
}) {
  return (
    <div
      className={cn(
        "flex items-center gap-1.5",
        align === "end" && "ml-auto",
        className,
      )}
    >
      {children}
    </div>
  );
}

/** Vertical separator between toolbar groups — same height as a row control. */
function ToolbarSeparator({ className }: { className?: string }) {
  return (
    <span
      aria-hidden
      className={cn("inline-block h-5 w-px bg-line-subtle mx-0.5", className)}
    />
  );
}

Toolbar.Group = ToolbarGroup;
Toolbar.Separator = ToolbarSeparator;
